import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import walletService from '../../services/walletService';

// --- Async Thunks ---

export const fetchEarningsSummary = createAsyncThunk(
  'earnings/fetchSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getEarningsSummary();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch earnings summary');
    }
  }
);

export const fetchEarningsHistory = createAsyncThunk(
  'earnings/fetchHistory',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getEarningsHistory(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch earnings history');
    }
  }
);

export const fetchPayoutEligibility = createAsyncThunk(
  'earnings/fetchEligibility',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getPayoutEligibility();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch payout eligibility');
    }
  }
);

export const fetchPayoutPreview = createAsyncThunk(
  'earnings/fetchPayoutPreview',
  async (amount, { rejectWithValue }) => {
    try {
      const response = await walletService.getPayoutPreview(amount);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch payout preview');
    }
  }
);

export const submitPAN = createAsyncThunk(
  'earnings/submitPAN',
  async (panNumber, { dispatch, rejectWithValue }) => {
    try {
      const response = await walletService.updatePAN(panNumber);
      dispatch(fetchPayoutEligibility());
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to save PAN');
    }
  }
);

export const fetchAdminRevenueSummary = createAsyncThunk(
  'earnings/fetchAdminRevenueSummary',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getAdminRevenueSummary(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch revenue summary');
    }
  }
);

export const fetchActiveConsultations = createAsyncThunk(
  'earnings/fetchActiveConsultations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await walletService.getActiveConsultations();
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch active consultations');
    }
  }
);

export const fetchAdminAllPayouts = createAsyncThunk(
  'earnings/fetchAdminAllPayouts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getAdminAllPayouts(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch payouts');
    }
  }
);

export const fetchAstrologerEarningsList = createAsyncThunk(
  'earnings/fetchAstrologerEarningsList',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await walletService.getAstrologerEarningsList(params);
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch astrologer earnings');
    }
  }
);

// --- Slice ---

const earningsSlice = createSlice({
  name: 'earnings',
  initialState: {
    summary: null,
    history: { transactions: [], has_more: false, next_cursor: null, total: 0 },
    eligibility: null,
    payoutPreview: null,
    adminRevenue: null,
    adminPayouts: { payouts: [], pagination: {} },
    astrologerList: { astrologers: [], pagination: {} },
    activeConsultations: { consultations: [], count: 0 },
    loading: {
      summary: false,
      history: false,
      eligibility: false,
      preview: false,
      pan: false,
      adminRevenue: false,
      adminPayouts: false,
      astrologerList: false,
      activeConsultations: false,
    },
    error: {
      summary: null,
      history: null,
      eligibility: null,
      preview: null,
      pan: null,
      adminRevenue: null,
      adminPayouts: null,
      astrologerList: null,
      activeConsultations: null,
    },
    panSuccess: null,
  },
  reducers: {
    clearPayoutPreview: (state) => { state.payoutPreview = null; },
    clearPanSuccess: (state) => { state.panSuccess = null; },
  },
  extraReducers: (builder) => {
    // Summary
    builder
      .addCase(fetchEarningsSummary.pending, (state) => { state.loading.summary = true; state.error.summary = null; })
      .addCase(fetchEarningsSummary.fulfilled, (state, action) => { state.loading.summary = false; state.summary = action.payload; })
      .addCase(fetchEarningsSummary.rejected, (state, action) => { state.loading.summary = false; state.error.summary = action.payload; });

    // History
    builder
      .addCase(fetchEarningsHistory.pending, (state) => { state.loading.history = true; state.error.history = null; })
      .addCase(fetchEarningsHistory.fulfilled, (state, action) => {
        state.loading.history = false;
        if (action.meta.arg?.cursor) {
          // Append for pagination
          state.history.transactions = [...state.history.transactions, ...(action.payload.transactions || [])];
        } else {
          state.history = action.payload;
        }
      })
      .addCase(fetchEarningsHistory.rejected, (state, action) => { state.loading.history = false; state.error.history = action.payload; });

    // Eligibility
    builder
      .addCase(fetchPayoutEligibility.pending, (state) => { state.loading.eligibility = true; })
      .addCase(fetchPayoutEligibility.fulfilled, (state, action) => { state.loading.eligibility = false; state.eligibility = action.payload; })
      .addCase(fetchPayoutEligibility.rejected, (state, action) => { state.loading.eligibility = false; state.error.eligibility = action.payload; });

    // Payout preview
    builder
      .addCase(fetchPayoutPreview.pending, (state) => { state.loading.preview = true; state.error.preview = null; })
      .addCase(fetchPayoutPreview.fulfilled, (state, action) => { state.loading.preview = false; state.payoutPreview = action.payload; })
      .addCase(fetchPayoutPreview.rejected, (state, action) => { state.loading.preview = false; state.error.preview = action.payload; });

    // PAN
    builder
      .addCase(submitPAN.pending, (state) => { state.loading.pan = true; state.error.pan = null; state.panSuccess = null; })
      .addCase(submitPAN.fulfilled, (state, action) => { state.loading.pan = false; state.panSuccess = action.payload; })
      .addCase(submitPAN.rejected, (state, action) => { state.loading.pan = false; state.error.pan = action.payload; });

    // Admin revenue
    builder
      .addCase(fetchAdminRevenueSummary.pending, (state) => { state.loading.adminRevenue = true; })
      .addCase(fetchAdminRevenueSummary.fulfilled, (state, action) => { state.loading.adminRevenue = false; state.adminRevenue = action.payload; })
      .addCase(fetchAdminRevenueSummary.rejected, (state, action) => { state.loading.adminRevenue = false; state.error.adminRevenue = action.payload; });

    // Admin payouts
    builder
      .addCase(fetchAdminAllPayouts.pending, (state) => { state.loading.adminPayouts = true; })
      .addCase(fetchAdminAllPayouts.fulfilled, (state, action) => { state.loading.adminPayouts = false; state.adminPayouts = action.payload; })
      .addCase(fetchAdminAllPayouts.rejected, (state, action) => { state.loading.adminPayouts = false; state.error.adminPayouts = action.payload; });

    // Astrologer list
    builder
      .addCase(fetchAstrologerEarningsList.pending, (state) => { state.loading.astrologerList = true; })
      .addCase(fetchAstrologerEarningsList.fulfilled, (state, action) => { state.loading.astrologerList = false; state.astrologerList = action.payload; })
      .addCase(fetchAstrologerEarningsList.rejected, (state, action) => { state.loading.astrologerList = false; state.error.astrologerList = action.payload; });

    // Active consultations
    builder
      .addCase(fetchActiveConsultations.pending, (state) => { state.loading.activeConsultations = true; })
      .addCase(fetchActiveConsultations.fulfilled, (state, action) => { state.loading.activeConsultations = false; state.activeConsultations = action.payload; })
      .addCase(fetchActiveConsultations.rejected, (state, action) => { state.loading.activeConsultations = false; state.error.activeConsultations = action.payload; });
  },
});

export const { clearPayoutPreview, clearPanSuccess } = earningsSlice.actions;
export default earningsSlice.reducer;
